/**
 * R12 landing page behaviour.
 *
 * - <r12-offer>        bag picker; adds the chosen option to the cart and goes straight to checkout
 * - <r12-sticky-box>   desktop offer box that stays in view next to the article
 * - <r12-mobile-bar>   phone buy bar: shows after the summary box, hides while an offer box is on screen
 * - any link ending in #r12-checkout checks out with the option currently picked in the first offer box
 *
 * Cart calls follow the Shopify Cart Ajax API (POST cart/clear.js, POST cart/add.js). Selling plans
 * (subscriptions) are sent with the line item because cart permalinks do not support them.
 */
(function () {
  'use strict';

  if (window.__r12LandingLoaded) return;
  window.__r12LandingLoaded = true;

  var CHECKOUT_HASH = '#r12-checkout';

  function inEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function rootUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  // Enabling the offer setting anywhere on this page applies to every landing-page purchase.
  function prepareCart() {
    return document.querySelector('r12-offer[data-clear-cart="true"]')
      ? postJSON('cart/clear.js')
      : Promise.resolve();
  }

  function postJSON(path, body) {
    return fetch(rootUrl() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok) {
            var error = new Error(data.description || data.message || '');
            error.status = response.status;
            throw error;
          }
          return data;
        });
    });
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  // variant.plan:quantity or variant:quantity; keep gift lines one-time unless a plan is explicit.
  function parseBundle(source) {
    var lines = source.split(',');
    return lines.map(function (line) {
      var match = line.trim().match(/^(\d+)(?:\.(\d+))?:(\d+)$/);
      if (!match || !Number.isSafeInteger(Number(match[1])) || Number(match[1]) <= 0 ||
          !Number.isSafeInteger(Number(match[3])) || Number(match[3]) <= 0 ||
          (match[2] && (!Number.isSafeInteger(Number(match[2])) || Number(match[2]) <= 0))) {
        throw new Error('This bundle is not available yet. Please contact us for help.');
      }
      var item = { id: Number(match[1]), quantity: Number(match[3]) };
      if (match[2]) item.selling_plan = Number(match[2]);
      return item;
    });
  }

  /* ---------------------------------------------------------------------
     Offer box
     --------------------------------------------------------------------- */
  class R12Offer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.ready = true;
      this.busy = false;
      this.button = this.form.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-r12-error]');
      this.buttonLabel = this.button ? this.button.innerHTML : '';

      this.form.addEventListener('change', this.onChange.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.refresh();
    }

    get options() {
      return toArray(this.form.querySelectorAll('input[name="r12_option"]'));
    }

    get selected() {
      var options = this.options;
      for (var i = 0; i < options.length; i++) {
        if (options[i].checked) return options[i];
      }
      return options[0] || null;
    }

    hidden(name) {
      var input = this.form.querySelector('input[type="hidden"][name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        this.form.appendChild(input);
      }
      return input;
    }

    /* Keeps the selected styling and the no-JS fallback fields in step with the picked radio. */
    refresh() {
      var selected = this.selected;
      this.options.forEach(function (option) {
        var row = option.closest('.r12-option');
        if (row) row.classList.toggle('is-selected', option === selected);
      });
      if (!selected) return;
      this.hidden('id').value = selected.getAttribute('data-variant-id') || '';
      this.hidden('quantity').value = selected.getAttribute('data-quantity') || '1';
      this.hidden('selling_plan').value = selected.getAttribute('data-selling-plan') || '';
    }

    select(value) {
      var match = this.options.filter(function (option) {
        return option.value === value;
      })[0];
      if (match && !match.checked) {
        match.checked = true;
        this.refresh();
      }
    }

    onChange(event) {
      if (!event.target || event.target.name !== 'r12_option') return;
      this.refresh();
      this.clearError();
      var value = event.target.value;
      var self = this;
      toArray(document.querySelectorAll('r12-offer')).forEach(function (other) {
        if (other !== self && typeof other.select === 'function') other.select(value);
      });
    }

    onSubmit(event) {
      event.preventDefault();
      if (inEditor()) return;
      if (this.busy) return;
      var submitter = event.submitter;
      if (submitter && submitter.hasAttribute('data-r12-bundle-choice')) {
        this.select(submitter.getAttribute('data-r12-bundle-choice'));
        this.button = submitter;
        this.buttonLabel = submitter.innerHTML;
      }
      this.checkout(submitter || this.button);
    }

    setBusy(state, trigger) {
      this.busy = state;
      var targets = [this.button, trigger || this.trigger].filter(Boolean);
      this.trigger = state ? trigger || null : null;
      targets.forEach(function (el) {
        if (state) el.setAttribute('aria-busy', 'true');
        else el.removeAttribute('aria-busy');
      });
      if (!this.button) return;
      var loading = this.getAttribute('data-loading-label');
      if (state && loading) this.button.textContent = loading;
      if (!state) this.button.innerHTML = this.buttonLabel;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.getAttribute('data-error-message') || '';
      this.errorEl.hidden = false;
      if (typeof this.errorEl.scrollIntoView === 'function') {
        this.errorEl.scrollIntoView({ block: 'center' });
      }
    }

    /* Sends the buyer to checkout with the currently picked option. */
    checkout(trigger) {
      if (inEditor()) return;
      var option = this.selected;
      if (this.busy || !option) return;
      this.clearError();

      var customUrl = option.getAttribute('data-url');
      if (customUrl) {
        this.setBusy(true, trigger);
        var customOffer = this;
        prepareCart().then(function () {
          window.location.assign(customUrl);
        }).catch(function (error) {
          customOffer.setBusy(false);
          customOffer.showError(error && error.message);
        });
        return;
      }

      var bundleSource = (option.getAttribute('data-bundle-items') || '').trim();
      var variantId = option.getAttribute('data-variant-id');
      if ((!bundleSource && option.getAttribute('data-bundle-required') === 'true') || (!bundleSource && !variantId)) {
        this.showError(this.getAttribute('data-unavailable-message'));
        return;
      }

      var item = {
        id: Number(variantId),
        quantity: Math.max(1, parseInt(option.getAttribute('data-quantity'), 10) || 1),
      };
      var sellingPlan = option.getAttribute('data-selling-plan');
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      var items;
      try {
        items = bundleSource ? parseBundle(bundleSource) : [item];
      } catch (error) {
        this.showError(error.message);
        return;
      }

      var discount = option.getAttribute('data-discount');
      var self = this;

      this.setBusy(true, trigger);

      prepareCart()
        .then(function () {
          return postJSON('cart/add.js', { items: items });
        })
        .then(function () {
          var destination = '/checkout';
          if (discount) {
            destination = '/discount/' + encodeURIComponent(discount) + '?redirect=' + encodeURIComponent('/checkout');
          }
          window.location.assign(destination);
        })
        .catch(function (error) {
          self.setBusy(false);
          self.showError(error && error.message);
        });
    }
  }

  /* ---------------------------------------------------------------------
     Sticky desktop box: tells the section how tall the box is so the
     section can stick without pushing the article down.
     --------------------------------------------------------------------- */
  class R12StickyBox extends HTMLElement {
    connectedCallback() {
      var self = this;
      var section = this.closest('.shopify-section');
      if (!section) return;
      this.update = function () {
        section.style.setProperty('--r12-sidebar-h', self.offsetHeight + 'px');
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this);
      }
      window.addEventListener('resize', this.update);
      window.addEventListener('load', this.update);
      this.update();
      this.watchAway();
      var revealSelector = this.getAttribute('data-r12-reveal-after');
      var revealTarget = revealSelector && document.querySelector(revealSelector);
      if (revealSelector) {
        this.revealUpdate = function () {
          var introduced = !!revealTarget && revealTarget.getBoundingClientRect().top < window.innerHeight;
          self.classList.toggle('is-introduced', introduced);
          if (introduced) self.removeAttribute('inert');
          else self.setAttribute('inert', '');
        };
        window.addEventListener('scroll', this.revealUpdate, { passive: true });
        window.addEventListener('resize', this.revealUpdate);
        this.revealUpdate();
      }
    }

    /* Fades the box out while a full-width footer (marked data-r12-hide-sticky) is on screen. */
    watchAway() {
      var self = this;
      if (this.awayObserver) this.awayObserver.disconnect();
      this.classList.remove('is-away');
      if (!('IntersectionObserver' in window)) return;
      var inView = [];
      this.awayObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var index = inView.indexOf(entry.target);
          if (entry.isIntersecting && index === -1) inView.push(entry.target);
          if (!entry.isIntersecting && index !== -1) inView.splice(index, 1);
        });
        self.classList.toggle('is-away', inView.length > 0);
      });
      toArray(document.querySelectorAll('[data-r12-hide-sticky]')).forEach(function (el) {
        self.awayObserver.observe(el);
      });
    }

    disconnectedCallback() {
      if (this.awayObserver) this.awayObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
      window.removeEventListener('load', this.update);
      if (this.revealUpdate) {
        window.removeEventListener('scroll', this.revealUpdate);
        window.removeEventListener('resize', this.revealUpdate);
      }
    }
  }

  /* ---------------------------------------------------------------------
     Mobile buy bar
     --------------------------------------------------------------------- */
  class R12MobileBar extends HTMLElement {
    connectedCallback() {
      this.forced = false;
      this.pastSummary = false;
      this.offersInView = [];
      this.watch();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.summaryObserver) this.summaryObserver.disconnect();
      if (this.revealUpdate) {
        window.removeEventListener('scroll', this.revealUpdate);
        window.removeEventListener('resize', this.revealUpdate);
      }
    }

    watch() {
      var self = this;
      if (this.observer) this.observer.disconnect();
      if (this.summaryObserver) this.summaryObserver.disconnect();
      this.offersInView = [];
      if (this.revealUpdate) {
        window.removeEventListener('scroll', this.revealUpdate);
        window.removeEventListener('resize', this.revealUpdate);
      }
      var revealSelector = this.getAttribute('data-r12-reveal-after');
      var revealTarget = revealSelector && document.querySelector(revealSelector);
      if (revealSelector) {
        this.revealUpdate = function () {
          self.pastSummary = !!revealTarget && revealTarget.getBoundingClientRect().top < window.innerHeight;
          self.render();
        };
        window.addEventListener('scroll', this.revealUpdate, { passive: true });
        window.addEventListener('resize', this.revealUpdate);
        this.revealUpdate();
      }

      if (!('IntersectionObserver' in window)) {
        if (!revealSelector) this.pastSummary = true;
        this.render();
        return;
      }

      var summary = revealSelector ? null : document.querySelector('[data-r12-summary]');
      if (!revealSelector) this.pastSummary = !summary;

      /* The summary counts as "passed" once it is fully above the screen. The observed area reaches
         far below the screen, so this is right from the first reading: also when the summary starts
         below the fold, after a jump link, or when the browser restores the scroll position. */
      if (summary) {
        this.summaryObserver = new IntersectionObserver(
          function (entries) {
            self.pastSummary = !entries[entries.length - 1].isIntersecting;
            self.render();
          },
          { rootMargin: '0px 0px 1000000px 0px' }
        );
        this.summaryObserver.observe(summary);
      }

      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var index = self.offersInView.indexOf(entry.target);
          if (entry.isIntersecting && index === -1) self.offersInView.push(entry.target);
          if (!entry.isIntersecting && index !== -1) self.offersInView.splice(index, 1);
        });
        self.render();
      });

      toArray(document.querySelectorAll('[data-r12-offer]')).forEach(function (offer) {
        self.observer.observe(offer);
      });
      this.render();
    }

    render() {
      var visible = this.forced || (this.pastSummary && this.offersInView.length === 0);
      this.classList.toggle('is-visible', visible);
      if (visible) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
    }
  }

  if (!customElements.get('r12-offer')) customElements.define('r12-offer', R12Offer);
  if (!customElements.get('r12-sticky-box')) customElements.define('r12-sticky-box', R12StickyBox);
  if (!customElements.get('r12-mobile-bar')) customElements.define('r12-mobile-bar', R12MobileBar);

  /* Any link that ends in #r12-checkout checks out with the first offer box on the page.
     Without JavaScript the same link simply jumps to that offer box. */
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;
    var binding = link.getAttribute('data-r12-cta-items');
    var href = link.getAttribute('href') || '';
    if (inEditor() && (binding || href.slice(-CHECKOUT_HASH.length) === CHECKOUT_HASH || link.matches('.r12-btn, .r12-link'))) {
      event.preventDefault();
      return;
    }
    if (binding) {
      event.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      var message = link.nextElementSibling;
      if (!message || !message.hasAttribute('data-r12-cta-error')) {
        message = document.createElement('p');
        message.setAttribute('data-r12-cta-error', '');
        message.setAttribute('role', 'alert');
        link.insertAdjacentElement('afterend', message);
      }
      message.hidden = true;
      function fail(error) {
        link.removeAttribute('aria-busy');
        message.textContent = error.message || 'Unable to open checkout. Please try again.';
        message.hidden = false;
      }
      var items;
      try { items = parseBundle(binding); } catch (error) { fail(error); return; }
      link.setAttribute('aria-busy', 'true');
      var noCoupon = link.getAttribute('data-r12-cta-no-coupon') === 'true';
      var coupon = noCoupon ? '' : link.getAttribute('data-r12-cta-coupon');
      prepareCart().then(function () {
        return postJSON('cart/add.js', { items: items });
      }).then(function () {
        if (noCoupon) return postJSON('cart/update.js', { discount: '' });
      }).then(function () {
        var checkout = rootUrl() + 'checkout';
        window.location.assign(coupon ? rootUrl() + 'discount/' + encodeURIComponent(coupon) + '?redirect=' + encodeURIComponent(checkout) : checkout);
      }).catch(fail);
      return;
    }
    if (href.slice(-CHECKOUT_HASH.length) !== CHECKOUT_HASH) return;
    var offer = document.querySelector('r12-offer');
    if (!offer || typeof offer.checkout !== 'function' || !offer.ready) return;
    event.preventDefault();
    offer.checkout(link);
  });

  /* Coming back with the browser Back button: put the buttons back to normal. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    toArray(document.querySelectorAll('[data-r12-cta-items][aria-busy]')).forEach(function (link) {
      link.removeAttribute('aria-busy');
    });
    toArray(document.querySelectorAll('r12-offer')).forEach(function (offer) {
      if (typeof offer.setBusy === 'function' && offer.ready) offer.setBusy(false);
    });
  });

  /* Theme editor support */
  if (inEditor()) {
    var rewatchBars = function () {
      toArray(document.querySelectorAll('r12-mobile-bar')).forEach(function (bar) {
        if (typeof bar.watch === 'function') bar.watch();
      });
      toArray(document.querySelectorAll('r12-sticky-box')).forEach(function (box) {
        if (typeof box.watchAway === 'function') box.watchAway();
      });
    };

    document.addEventListener('shopify:section:load', rewatchBars);
    document.addEventListener('shopify:section:unload', function () {
      window.setTimeout(rewatchBars, 0);
    });

    document.addEventListener('shopify:section:select', function (event) {
      var bar = event.target.querySelector('r12-mobile-bar');
      if (bar) {
        bar.forced = true;
        bar.render();
      }
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      var bar = event.target.querySelector('r12-mobile-bar');
      if (bar) {
        bar.forced = false;
        bar.render();
      }
    });

    document.addEventListener('shopify:block:select', function (event) {
      if (event.target.matches && event.target.matches('details.r12-faq__item')) {
        event.target.open = true;
      }
    });
  }
})();
